/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import Dynamic from 'next/dynamic';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CustomLink from 'components/Buttons/CustomLinkComponent';
import LabelWithDescriptionComponent from 'components/SchoolsComponents/LabelWithDescriptionComponent';
import PriceComponent from 'components/PriceComponent';
import UnlockEstimateComponent from 'components/UnlockEstimateComponent';

import { buildUrl } from 'utils/urlUtils';
import { getUpcomingStartDatesText } from 'utils/courseUtils';
import { shouldUnlockPrice, showScheduledButton } from 'utils/lead';
import { switchCommissionBackground } from 'utils/schoolUtils';
import cloudinaryLoader from 'utils/loaderUtils';

const ScheduledMeetingButtonComponent = Dynamic(() =>
  import('components/SchoolsComponents/ScheduledMeetingButtonComponent')
);

const categoriesSlugInfo = {
  Bronze: 'Escola econômica para caber no seu bolso',
  Gold: 'Escola reconhecida com alta qualidade',
  Silver: 'Escola com bom custo benefício',
};

SchoolItemComponent.propTypes = {
  school: PropTypes.instanceOf(Object).isRequired,
  isSalesExecutive: PropTypes.bool.isRequired,
  isLeadUnlocked: PropTypes.bool,
  lead: PropTypes.instanceOf(Object),
};

export default function SchoolItemComponent({
  school: {
    accommodationDesc,
    accommodationId,
    accommodationType,
    bedroomType,
    category,
    cityName,
    citySlugName,
    classesQuantity,
    commissionCategory,
    countryName,
    countrySlugName,
    courseId,
    courseType,
    discount,
    hasSupplement,
    id,
    internalName,
    period,
    price,
    schoolSlugName,
    startDates,
    title,
    weekRange,
  },
  isSalesExecutive,
  isLeadUnlocked = false,
  lead = {},
  schoolItemIndex,
}) {
  const theme = useTheme();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useUser();
  const valuesFilter = useSelector((state) => state.schoolFilterReducer);

  const unlockPrice = shouldUnlockPrice(lead, user);

  const [imageHasError, setImageHasError] = useState(false);

  const isCampaign = ['A', 'B'].includes(commissionCategory);

  const queryParams = {
    accommodationId,
    bedroomType,
    courseId,
    startDate: valuesFilter.startDate,
    weekRange,
  };

  return (
    <Paper
      elevation={0}
      key={id}
      sx={{
        background: isCampaign
          ? 'linear-gradient(270deg, #258DBF 20%, #054F77 100%)'
          : '',
        mb: 2,
        border: schoolItemIndex === 0 && mobileBreakpoint && 'none',
        borderTop:
          schoolItemIndex === 0 &&
          mobileBreakpoint &&
          '1px solid rgba(0, 0, 0, 0.12)',
        pr: { xs: 0, sm: 2 },
        color: isCampaign ? 'white' : '',
      }}
      variant="outlined"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <CustomLink
            rel="noopener noreferrer"
            target="_blank"
            href={buildUrl(
              [countrySlugName, citySlugName, schoolSlugName, id],
              queryParams
            )}
          >
            <Box
              height={{ xs: 196, sm: '100%' }}
              position="relative"
              width="100%"
            >
              <Image
                alt={schoolSlugName}
                fill
                loader={cloudinaryLoader}
                onError={() => setImageHasError(true)}
                src={imageHasError ? '1' : `schools/0${id}/01`}
                style={{
                  borderRadius: mobileBreakpoint ? '3px 3px 0 0' : '22px',
                  objectFit: 'cover',
                  padding: !mobileBreakpoint && 18,
                }}
              />
            </Box>
          </CustomLink>
        </Grid>

        <Grid item xs={12} sm={6} sx={{ my: { sm: 2 }, mx: { xs: 2, sm: 0 } }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: { xs: 'space-between', md: 'initial' },
            }}
          >
            <Typography component="h2" fontWeight={700} variant="h5">
              <CustomLink
                rel="noopener noreferrer"
                target="_blank"
                href={buildUrl(
                  [countrySlugName, citySlugName, schoolSlugName, id],
                  queryParams
                )}
                sx={{
                  color: isCampaign ? 'white' : '',
                }}
              >
                {internalName}
              </CustomLink>
            </Typography>

            {isCampaign && (
              <Box height={50} position="relative" width={105}>
                <Image
                  alt="Preço de 2024"
                  fill
                  loader={({ src, width }) => `${src}?w=${width}`}
                  src="https://res.cloudinary.com/intercambiodireto-com/image/upload/q_auto:good/v1744403956/Others/Campaigns/business-tag_ojkzdk"
                  style={{
                    objectFit: 'contain',
                    marginLeft: mobileBreakpoint ? 0 : 16,
                  }}
                />
              </Box>
            )}
          </Box>

          <Box display="flex" mr={1}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  textAlign: 'left',
                  display: 'inline',
                  textTransform: 'capitalize',
                }}
                variant="subtitle1"
              >
                {cityName}
                {countrySlugName ? `, ${countryName}` : null}
              </Typography>
            </Grid>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1} mt={1} mb={1}>
            {isSalesExecutive && commissionCategory && (
              <Box>
                <Chip
                  sx={{
                    backgroundColor:
                      switchCommissionBackground(commissionCategory),
                    color: 'white',
                    px: 2,
                  }}
                  label={commissionCategory}
                  size="small"
                />
              </Box>
            )}

            <Box>
              <LabelWithDescriptionComponent
                categories={category}
                categoriesSlugInfo={categoriesSlugInfo[category]}
              />
            </Box>

            {discount - price > 0 && (
              <Box>
                <Chip
                  size="small"
                  sx={{
                    color: isCampaign ? 'white' : '',
                    backgroundColor: isCampaign ? '#1283e6' : '',
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                  label="Desconto"
                />
              </Box>
            )}

            {hasSupplement && (
              <Box>
                <Chip
                  size="small"
                  sx={{
                    color: isCampaign ? 'white' : '',
                    backgroundColor: isCampaign ? '#1283e6' : '',
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                  label="Alta temporada"
                />
              </Box>
            )}
          </Box>

          <Divider />

          {courseType && (
            <Box mt={1} color="textPrimary" textAlign="left">
              <Typography
                display="inline"
                align="left"
                variant="caption"
                textTransform="uppercase"
              >
                <Box display="inline" fontWeight={500} letterSpacing={1}>
                  {courseType}&nbsp;
                  {title && `- ${title} `}
                </Box>
              </Typography>

              {period && (
                <>
                  <Typography display="inline" align="left" variant="caption">
                    -&nbsp;
                  </Typography>

                  <Tooltip
                    title={getUpcomingStartDatesText(startDates)}
                    placement="top"
                  >
                    <Typography display="inline" align="left" variant="body2">
                      {classesQuantity} por semana - {period}
                    </Typography>
                  </Tooltip>
                </>
              )}
            </Box>
          )}

          {accommodationType && (
            <Box textAlign="left">
              <Typography variant="caption" textTransform="uppercase">
                <Box display="inline" fontWeight={500} letterSpacing={1}>
                  {accommodationType}
                </Box>
              </Typography>

              {accommodationDesc && (
                <Typography variant="caption">
                  &nbsp;- {accommodationDesc}
                </Typography>
              )}
            </Box>
          )}
        </Grid>

        <Grid
          item
          sm={3}
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            my: 2,
            mx: { xs: 2, sm: 0 },
          }}
          xs={12}
        >
          <Box textAlign="center" width="100%" color="textPrimary">
            {discount - price > 0 && (
              <Typography
                sx={{
                  textDecoration: 'line-through',
                  color: isCampaign ? 'white' : '#8a8a8a',
                }}
                variant="h5"
              >
                <Box fontWeight={300}>
                  De&nbsp;
                  <PriceComponent value={discount} />
                </Box>
              </Typography>
            )}

            <Typography
              variant="h4"
              color={isCampaign ? 'white' : 'primary'}
              sx={{
                filter: !unlockPrice && 'blur(8px)',
                userSelect: !unlockPrice && 'none',
              }}
            >
              <Box fontWeight={800} letterSpacing={-1}>
                <PriceComponent value={price} />
              </Box>
            </Typography>

            <Typography variant="caption" display="block" gutterBottom my={1}>
              à vista com desconto <br /> ou em até 12x sem juros.
            </Typography>

            {!isLeadUnlocked ? (
              <UnlockEstimateComponent />
            ) : showScheduledButton(lead) ? (
              <ScheduledMeetingButtonComponent />
            ) : (
              <Button
                href={buildUrl(
                  [countrySlugName, citySlugName, schoolSlugName, id],
                  queryParams
                )}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{
                  backgroundColor: '#3e3232',
                  color: '#fff',
                  ':hover': {
                    backgroundColor: '#2a1d1d',
                  },
                }}
                variant="contained"
              >
                Conferir
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
